import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
} from 'recharts';
import { queryDataSource } from '../../api/query';
import { sourceIdBul } from '../../utils/raporSonek';
import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { demoTheme } from '../../theme';
import type { ReportConfig } from '../../types/panelConfig';
import type { Donem } from './donem';

interface GunlukSatiri {
  tarih: string;
  fis_sayisi: number;
  brut_ciro: number;
  iade_tutar: number;
  net_ciro: number;
}

interface SaatlikSatiri {
  saat: number;
  fis_sayisi: number;
  ciro: number;
}

interface Props {
  reports: ReportConfig[];
  donem: Donem;
}

interface Nokta {
  etiket: string;
  ciro: number;
}

/**
 * Zaman blogu donemin GRANULARITESINE gore kaynak degistirir: tek gunluk
 * donemde saatlik dagilim (002) daha bilgilendirici, cok gunluk donemde
 * gunluk trend (001) daha okunakli. Ikisi ASLA ayni anda sorgulanmaz - id
 * secimi donem.tekGun'e gore YAPILIR, digeri hic sorgulanmaz.
 */
export function ZamanBlok({ reports, donem }: Props) {
  const params = { '@BASTARIH': donem.bas, '@BITTARIH': donem.bit };
  const saatlikMi = donem.tekGun;

  const idGunluk = sourceIdBul(reports, 1);
  const idSaatlik = sourceIdBul(reports, 2);
  const id = saatlikMi ? idSaatlik : idGunluk;

  // Iki farkli satir sekli (gunluk/saatlik) tek sorgu kancasindan gecer -
  // TS'in tek bir queryFn donus tipi bekledigi icin sonuc birlesim tipiyle
  // alinir, sekil ayrimi asagida saatlikMi ile yapilir.
  const zamanQ = useQuery({
    queryKey: ['sa-zaman', saatlikMi, id, params],
    queryFn: () =>
      queryDataSource<GunlukSatiri | SaatlikSatiri>({ sourceId: id!, parameters: params }),
    enabled: !!id,
    throwOnError: false,
  });

  const noktalar = useMemo<Nokta[]>(() => {
    const rows = zamanQ.data?.data ?? [];
    if (saatlikMi) {
      return (rows as SaatlikSatiri[]).map((r) => ({
        etiket: `${String(r.saat).padStart(2, '0')}:00`,
        ciro: Number(r.ciro ?? 0),
      }));
    }
    return (rows as GunlukSatiri[]).map((r) => ({
      etiket: String(r.tarih),
      ciro: Number(r.net_ciro ?? 0),
    }));
  }, [zamanQ.data, saatlikMi]);

  if (!id) return null; // rapor yok -> sorgu bile acilmaz
  if (noktalar.length === 0) return null; // bos durum kurali: veri yoksa blok gizlenir

  const baslik = saatlikMi ? 'Saatlik Satış Dağılımı' : 'Ciro Trendi';

  return (
    <div data-testid="blok-zaman">
      <SectionCard
        title={baslik}
        subtitle={saatlikMi ? 'Günün saatlerine göre ciro' : 'Döneme yayılan günlük ciro'}
      >
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={noktalar} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={demoTheme.border} vertical={false} />
              <XAxis
                dataKey="etiket"
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={{ stroke: demoTheme.border }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={(v) => fmtNum(Number(v))}
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={false}
                tickLine={false}
                width={64}
              />
              <ChartTooltip formatter={(v) => fmtTRY(Number(v))} />
              <Line
                type="monotone"
                dataKey="ciro"
                stroke={demoTheme.primary}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>
    </div>
  );
}
